import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { OpeningHours } from '../types';

interface OpeningHoursContextType {
  openingHours: OpeningHours;
  setOpeningHours: React.Dispatch<React.SetStateAction<OpeningHours>>;
  loading: boolean;
  error: string | null;
}

const OpeningHoursContext = createContext<OpeningHoursContextType | undefined>(undefined);

export const useOpeningHours = () => {
  const context = useContext(OpeningHoursContext);
  if (context === undefined) {
    throw new Error('useOpeningHours must be used within an OpeningHoursProvider');
  }
  return context;
};

interface OpeningHoursProviderProps {
  children: React.ReactNode;
}

export const OpeningHoursProvider: React.FC<OpeningHoursProviderProps> = ({ children }) => {
  const [openingHours, setOpeningHours] = useState<OpeningHours>({
    monday: { open: '08:00', close: '17:00', closed: false },
    tuesday: { open: '08:00', close: '17:00', closed: false },
    wednesday: { open: '08:00', close: '17:00', closed: false },
    thursday: { open: '08:00', close: '17:00', closed: false },
    friday: { open: '08:00', close: '17:00', closed: false },
    saturday: { open: '08:00', close: '15:00', closed: false },
    sunday: { open: '08:00', close: '17:00', closed: true }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const settingsRef = doc(db, 'settings', 'openingHours');
    
    const unsubscribe = onSnapshot(
      settingsRef,
      (doc) => {
        setLoading(false);
        if (doc.exists()) {
          const data = doc.data();
          if (data.openingHours) {
            // Ensure all days have the closed property for backward compatibility
            const loadedHours = data.openingHours;
            const updatedHours: OpeningHours = {
              monday: { ...loadedHours.monday, closed: loadedHours.monday?.closed ?? false },
              tuesday: { ...loadedHours.tuesday, closed: loadedHours.tuesday?.closed ?? false },
              wednesday: { ...loadedHours.wednesday, closed: loadedHours.wednesday?.closed ?? false },
              thursday: { ...loadedHours.thursday, closed: loadedHours.thursday?.closed ?? false },
              friday: { ...loadedHours.friday, closed: loadedHours.friday?.closed ?? false },
              saturday: { ...loadedHours.saturday, closed: loadedHours.saturday?.closed ?? false },
              sunday: { ...loadedHours.sunday, closed: loadedHours.sunday?.closed ?? true }
            };
            setOpeningHours(updatedHours);
            setError(null);
          }
        } else {
          // Document doesn't exist, keep default hours
          setError(null);
        }
      },
      (error) => {
        console.error('Error listening to opening hours:', error);
        setError('Failed to load opening hours');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const value = {
    openingHours,
    setOpeningHours,
    loading,
    error
  };

  return (
    <OpeningHoursContext.Provider value={value}>
      {children}
    </OpeningHoursContext.Provider>
  );
};
